import Player from '@vimeo/player'
import clsx from 'clsx'
import React, { Dispatch, FC, SetStateAction, useCallback, useContext, useEffect, useRef, useState } from 'react'

import SVGPause from '@Assets/SVG/Pause'
import SVGPlay from '@Assets/SVG/Play'
import SVGReplay from '@Assets/SVG/Replay'
import videoStyles from '@Components/Product/Slider/Video.module.scss'
import { MetadataContext } from '@Globals/Metadata'

import styles from './VimeoPlayer.module.scss'

export interface VimeoPlayerOptions {
  autoplay?: boolean
  id?: string
  loop?: boolean
  muted?: boolean
}

export interface VimeoPlayerProps {
  className?: string
  enableControls?: boolean
  isVisible?: boolean
  onLoad?: Dispatch<SetStateAction<boolean>>
  onLoadingFinish?: () => void
  options?: VimeoPlayerOptions
  showTrendhimControls?: boolean
  videoID: string
}

const SubtitleTrackStoreMap: Record<string, string> = {
  1: 'da',
  3: 'no',
  4: 'sv',
  5: 'nl',
  6: 'fi',
  7: 'de',
  8: 'en-GB',
  9: 'cs',
  10: 'fr',
  11: 'nl-BE',
  12: 'fr-BE',
  14: 'es',
  15: 'it',
  16: 'pl',
  17: 'hu',
  18: 'de-AT',
  19: 'de-CH',
  20: 'fr-CH',
  21: 'en-IE',
  22: 'bg',
  23: 'el',
  24: 'pt',
  25: 'ro',
  26: 'sk',
  27: 'en-US',
  33: 'en-CA',
  34: 'fr-CA',
  36: 'es-419',
  13: 'en-GB',
  28: 'en-GB',
  29: 'en-GB',
  30: 'en-GB',
  31: 'en-GB',
  32: 'en-GB',
  35: 'en-GB',
}

export const VimeoPlayer: FC<VimeoPlayerProps> = (
  { className,
    enableControls = true,
    onLoad,
    options,
    isVisible = undefined,
    videoID,
    showTrendhimControls = false,
    onLoadingFinish },
) => {
  const { store } = useContext(MetadataContext)
  const vimeoPlayer = useRef<null | Player>(null)
  const [isPlaying, setPlaying] = useState(options?.autoplay)
  const loadStoreTrack = useCallback(async (player: Player) => {
    if (!player?.getTextTracks) {
      return
    }
    const textTracks: Array<{ language?: string }> = await player.getTextTracks()
    const track = textTracks.find(textTrack => textTrack.language === SubtitleTrackStoreMap[store.id])
    if (track?.language) {
      await player.enableTextTrack(track.language)
    }
  }, [store])

  const vimeoPlayerRef = useCallback(async (node) => {
    const video = vimeoPlayer.current
    if (node === null && !video) {
      return
    }
    const setAudio = () => {
      if (options?.muted) {
        vimeoPlayer.current?.setVolume(0)
      } else {
        vimeoPlayer.current?.setVolume(0.5)
      }
    }

    if (!vimeoPlayer.current) {
      // @ts-ignore
      vimeoPlayer.current = new Player(node, { ...options, controls: enableControls, id: videoID, quality: 'auto', responsive: true, autopause: false })
      setAudio()
    } else if ((await vimeoPlayer.current!.getVideoId()).toFixed() !== videoID) {
      await vimeoPlayer.current!.loadVideo(videoID)
      if (options?.autoplay) {
        await vimeoPlayer.current!.play()
      }
    }
    await loadStoreTrack(vimeoPlayer.current!)
    if (typeof onLoad === 'function' && vimeoPlayer.current) {
      vimeoPlayer.current!.on('loaded', async () => {
        onLoad(false)
        vimeoPlayer.current?.setCurrentTime(0)
      })
    }
    vimeoPlayer.current!.on('play', () => {
      if (onLoadingFinish) {
        if (options?.autoplay) {
          setPlaying(true)
        }
        onLoadingFinish()
      }
    })
  }, [enableControls, loadStoreTrack, onLoad, onLoadingFinish, options, videoID])

  const pauseUnpause = async () => {
    const player = vimeoPlayer.current
    if (!player) {
      return
    }
    try {
      const isPaused = await player.getPaused()
      if (isPaused) {
        await player.play()
        setPlaying(true)
      } else {
        await player.pause()
        setPlaying(false)
      }
    } catch {
      // ignore errors
    }
  }

  useEffect(() => {
    if (options?.autoplay && typeof isVisible !== 'undefined' && vimeoPlayer.current) {
      const playSomething = async () => {
        const player = vimeoPlayer.current
        if (!player) {
          return
        }
        try {
          if (isVisible) {
            await player.play()
          } else {
            await player.pause()
          }
        } catch {
          // ignore errors
        }
      }

      playSomething().then()
    }
  }, [options?.autoplay, isVisible])

  const handleReplayButton = async () => {
    if (!vimeoPlayer.current) {
      return
    }
    vimeoPlayer.current!.setCurrentTime(0)
    vimeoPlayer.current!.play()
    setPlaying(true)
  }
  return (
    <>
      <div
        className={clsx(className, styles.vimeoPlayerContainer, enableControls && styles.enableControls)}
        onClick={pauseUnpause}
        // @ts-expect-error - TODO: ref's can't be async
        ref={vimeoPlayerRef}
      />
      {!enableControls && showTrendhimControls && (
        <div className={videoStyles.videoControls}>
          <button className={videoStyles.videoControlButton} onClick={pauseUnpause} tabIndex={-1} type="button">
            {isPlaying ? <SVGPause /> : <SVGPlay />}
          </button>
          <button className={videoStyles.videoControlButton} onClick={handleReplayButton} tabIndex={-1} type="button">
            {' '}
            <SVGReplay />
          </button>
        </div>
      )}
    </>
  )
}
